import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { type MRT_PaginationState } from 'material-react-table'
import { getBlogPosts, deleteBlogPost, type IBlogPostWithAuthor } from '../../../api/blog'
import { BlogPostsTable } from './BlogPostsTable'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'

export const BlogPostsView = () => {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<IBlogPostWithAuthor[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPost, setSelectedPost] = useState<IBlogPostWithAuthor | null>(null)

  useEffect(() => {
    loadPosts()
  }, [pagination.pageIndex, pagination.pageSize])

  const loadPosts = async () => {
    try {
      setIsLoading(true)
      const page = pagination.pageIndex + 1
      const result = await getBlogPosts(page, pagination.pageSize)
      setPosts(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading blog posts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleView = (post: IBlogPostWithAuthor) => {
    // TODO: Navigate to blog post page
    window.open(`/blog/${post.slug}`, '_blank')
  }

  const handleEdit = (post: IBlogPostWithAuthor) => {
    navigate(`/blog/editor?id=${post.id}`)
  }

  const handleDelete = (post: IBlogPostWithAuthor) => {
    setSelectedPost(post)
    setDeleteDialogOpen(true)
  }

  const handleAddNew = () => {
    navigate('/blog/editor')
  }

  const confirmDelete = async () => {
    if (!selectedPost) return

    try {
      await deleteBlogPost(selectedPost.id)
      setDeleteDialogOpen(false)
      setSelectedPost(null)
      loadPosts()
    } catch (error) {
      console.error('Error deleting blog post:', error)
      alert('Failed to delete blog post. Please try again.')
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex items-center justify-between px-8 pb-6 pt-8 border-b border-searchBorder">
        <h1 className="text-2xl font-bold">Blog Posts</h1>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
        >
          + New Post
        </button>
      </div>

      {/* Material React Table */}
      <div className="flex-1 overflow-hidden">
        <BlogPostsTable
          data={posts}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setSelectedPost(null)
        }}
        onConfirm={confirmDelete}
        title="Delete Blog Post"
        content={`Are you sure you want to delete "${selectedPost?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  )
}
